import { getRefDrugs } from "@/api/lost";

/**
 * Configurazione della tabella/modale per i farmaci persi (lost_has_drug).
 *
 * Flusso di selezione:
 *  1. L'utente sceglie il farmaco dal dropdown (drug_id).
 *  2. Batch e Serial Number vengono precompilati automaticamente:
 *     - se il dato è già nel risultato della lista farmaci (es. type='return'),
 *       viene usato direttamente (fast path);
 *     - altrimenti viene eseguita una chiamata con drug_id per recuperare i lotti
 *       disponibili; se ce n'è uno solo, i campi vengono precompilati.
 *  3. Qty e Note vanno compilati manualmente.
 */

/**
 * Cache delle Promise di recupero lotti, indicizzata per
 * `{type}_{type_id}_{drug_id}`.
 * Evita chiamate duplicate quando lotto e serials chiedono gli stessi dati.
 */
const _lotsCache = {};

function fetchLotsForDrug(item) {
  const type = item.type || (item.lost && item.lost.type);
  const type_id = item.type_id || (item.lost && item.lost.type_id);
  const drug_id = item.drug_id;
  const id = item.lost_id;
  if (!type || !type_id || !drug_id) return Promise.resolve([]);

  const key = `${type}_${type_id}_${drug_id}`;
  if (!_lotsCache[key]) {
    _lotsCache[key] = getRefDrugs({ type, type_id, drug_id, id })
      .then((res) => res.data || [])
      .catch(() => []);
  }
  return _lotsCache[key];
}

export default {
  entityName: "lost_has_drug",
  title: "Drugs lost",
  pagination: {
    rowsPerPage: 10,
    page: 1,
    sortBy: "id",
    descending: true,
  },
  fields: [
    // ── Selezione farmaco ────────────────────────────────────────────────────
    {
      value: "drug_id",
      text: "Drug",
      type: "autocomplete",
      list:async (entity, search, item) =>  {
        let refDrugs = await getRefDrugs({
          type: item.type || (item.lost && item.lost.type),
          type_id: item.type_id || (item.lost && item.lost.type_id),
          id: item.lost_id,
          search,
        });
        if(refDrugs?.data && item.id) {
          let refDrug = refDrugs.data.find(d => d.drug_id == item.drug_id);
          item._drug_ref = refDrug;
        }
        return refDrugs;
      },
      title: "drug_name",
      subtitle: (item) => {
        const parts = [];
        if (item.lotto) parts.push(`Batch: ${item.lotto}`);
        if (item.serials) parts.push(`SN: ${item.serials}`);
        if (item.n_pieces != null) parts.push(`Qty: ${item.n_pieces}`);
        return parts.join(" | ");
      },
      id: "drug_id",
      table: true,
      validators: ["required"],
      readonly: (item) => !!item.id,
      small: true,
    },

    // Oggetto farmaco selezionato (nascosto — conserva has_serials e metadati)
    {
      value: "_drug_ref",
      type: "hidden",
      deps: ["drug_id"],
      default: (item, args, selected) => selected || null,
    },

    // Flag: ref priva di lotto/serial (per eventuale banner avviso in Upsert.vue)
    {
      value: "_ref_has_no_batch_serial",
      type: "hidden",
      deps: ["drug_id"],
      default: (item, args, selected) => {
        if (!selected) return false;
        return !selected.lotto && !selected.serials;
      },
    },

    // ── Batch ────────────────────────────────────────────────────────────────
    // Fast path: la lista farmaci include già il lotto (es. type='return').
    // Slow path: chiamata con drug_id per recuperare il lotto (es. type='request'
    // con un solo lotto nella siteresponse).
    {
      value: "lotto",
      text: "Batch",
      type: "text",
      table: true,
      small: true,
      deps: ["drug_id"],
      default: async (item, args, selected) => {
        if (selected && selected.lotto) return selected.lotto;
        const lots = await fetchLotsForDrug(item);
        return lots.length === 1 ? lots[0].lotto || "" : "";
      },
      validators: ["required"],
    },

    // ── Serial Number ─────────────────────────────────────────────────────────
    // Stessa logica del lotto: fast path dal selected, slow path con fetch.
    {
      value: "serials",
      text: "Serial Number",
      type: "text",
      table: true,
      small: true,
      deps: ["drug_id"],
      default: async (item, args, selected) => {
        if (selected && selected.serials) return selected.serials;
        const lots = await fetchLotsForDrug(item);
        return lots.length === 1 ? lots[0].serials || "" : "";
      },
    },

    // ── Qty ──────────────────────────────────────────────────────────────────
    {
      value: "n_pieces",
      text: "Qty",
      type: "number",
      table: true,
      mini: true,
      deps: ["drug_id"],
      default: (item, args, selected) =>
        (selected && selected.n_pieces) || null,
      validators: [
        "required",
        (field, value) => Number(value) > 0 || "Quantity must be > 0",
        (field, value, item) => {
          if (!item._drug_ref || item._drug_ref.available_qty == null)
            return true;
          return (
            Number(value) <= Number(item._drug_ref.available_qty) ||
            `Max quantity: ${item._drug_ref.available_qty}`
          );
        },
      ],
    },

    // Quantità disponibile (nascosta — valorizzata dal farmaco per i return)
    {
      value: "_available_qty",
      type: "hidden",
      deps: ["drug_id"],
      default: (item, args, selected) =>
        selected && selected.available_qty != null
          ? selected.available_qty
          : null,
    },

    // ── Note libere ──────────────────────────────────────────────────────────
    {
      full: true,
      value: "note",
      text: "Note",
      type: "textarea",
    },
  ],

  actions: [
    { value: "upsert", text: "Edit", color: "primary", icon: "mdi-pencil" },
    { value: "remove", text: "Delete", color: "error", icon: "mdi-delete" },
  ],
};
